import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { useSelector } from 'react-redux';
import i18n from 'i18n-js';
import { Typography, Divider, List, ListItem, Box, styled, Stack } from '@mui/material';
import { Card } from 'components/UI';

const VehicleStatus = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'status',
})(({ theme, status }) => {
  let color = theme.palette.text.primary;
  let backgroundColor = theme.palette.text.primary;

  switch (status?.toLowerCase()) {
    case 'active':
      color = theme.palette.success.main;
      backgroundColor = theme.palette.success.main;
      break;
    case 'inactive':
      color = theme.palette.grey[500];
      backgroundColor = theme.palette.grey[500];
      break;
    case 'blocked':
      color = theme.palette.error.main;
      backgroundColor = theme.palette.error.main;
      break;
    case 'unsharing':
      color = theme.palette.warning.main;
      backgroundColor = theme.palette.warning.main;
      break;
    default:
      break;
  }

  return {
    fontSize: '12px',
    position: 'relative',
    color: color,
    display: 'inline-block',
    '&::before': {
      content: "''",
      position: 'absolute',
      width: 5,
      height: 5,
      borderRadius: '50%',
      left: '-10px',
      bottom: '5px',
      backgroundColor: backgroundColor,
    },
  };
});

const getVehicleStatus = (status) => {
  return (
    <VehicleStatus variant="inherit" status={status}>
      {i18n.t(`vehicle_groups.status.${status}`, {
        defaultValue: status || '',
      })}
    </VehicleStatus>
  );
};

const VehicleServices = ({ services = [], menuOptions = [] }) => {
  const localeFormat = useSelector((state) => state.organizations?.localeFormat);

  const servicesList =
    services
      ?.filter(
        (service) =>
          service?.organization?.org_type === 'service' ||
          service?.organization?.org_type === 'business'
      )
      ?.reduce((acc, service) => {
        if (acc[service?.organization.id]) {
          acc[service?.organization?.id].push(service);
        } else {
          acc[service?.organization?.id] = [];
          acc[service?.organization?.id].push(service);
        }
        return acc;
      }, {}) || {};

  return (
    <Card
      menuItems={menuOptions}
      title="Assigned Services"
      data-cy="assined-service-widget-card"
      headerActionType="vertical"
    >
      <List
        component="nav"
        aria-labelledby="nested-list-subheader"
        data-cy="assined-service-widget-container"
      >
        {Object.keys(servicesList).length > 0 ? (
          Object.keys(servicesList).map((serviceId, index) => {
            const firstService = servicesList[serviceId][0];
            const serviceObj = servicesList[serviceId];
            return (
              <Fragment key={index}>
                <ListItem
                  sx={{
                    flexWrap: 'wrap',
                    color: 'text.secondary',
                    lineHeight: '3rem',
                  }}
                >
                  <Box
                    sx={{
                      width: 1,
                      fontWeight: 700,
                      fontSize: 13,
                      lineHeight: '15px',
                    }}
                  >
                    {firstService?.organization?.name}
                  </Box>
                  {serviceObj.map((service) => {
                    const endTime = moment(service.end_time);
                    const startTime = moment(service.start_time);
                    return (
                      <Stack
                        key={service.or || service.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          width: 1,
                          fontSize: 13,
                          lineHeight: '15px',
                          minHeight: '2rem',
                        }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          {`${i18n.t('common.from_start_time', {
                            startTime: startTime.format(localeFormat),
                          })} ${
                            service.end_time
                              ? i18n.t('common.till_end_time', {
                                  endTime: endTime.format(localeFormat),
                                })
                              : ''
                          }`}
                        </Box>
                        <Box sx={{ flexGrow: 0 }}>{getVehicleStatus(service?.status) || '--'}</Box>
                      </Stack>
                    );
                  })}
                </ListItem>
                <Divider />
              </Fragment>
            );
          })
        ) : (
          <ListItem>{i18n.t('vehicle.empty_services')}</ListItem>
        )}
      </List>
    </Card>
  );
};

VehicleServices.propTypes = {
  services: PropTypes.array,
  menuOptions: PropTypes.array,
};

export default VehicleServices;
